import React, { useState } from 'react';
import { Volume2, Eye } from './Icons';
import { speakTextWithFallback } from '../utils/voiceManager';

const VocabularyCard = ({ 
  word, 
  onStudyClick, 
  onTestClick, 
  language = 'traditional',
  mode = 'read' 
}) => {
  // Pronunciation and recording states
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingText, setPlayingText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  
  const getDisplayText = () => {
    switch (language) {
      case 'traditional':
        return {
          primary: word.traditional,
          secondary: word.english,
          pronunciation: word.pronunciation
        };
      case 'simplified':
        return {
          primary: word.simplified,
          secondary: word.english,
          pronunciation: word.pronunciation
        };
      case 'english':
        return {
          primary: word.english,
          secondary: word.traditional,
          pronunciation: word.pronunciation
        };
      default:
        return {
          primary: word.english,
          secondary: word.traditional,
          pronunciation: word.pronunciation
        };
    }
  };

  const displayText = getDisplayText();

  // Enhanced Text-to-Speech function with preferred voice support
  const speakText = (text) => {
    speakTextWithFallback(
      text,
      null, // availableVoices will be loaded by the voice manager
      (playingText) => {
        setIsPlaying(true);
        setPlayingText(playingText);
      },
      () => {
        setIsPlaying(false);
        setPlayingText('');
      },
      (event) => {
        setIsPlaying(false);
        setPlayingText('');
        if (event && event.error === 'not-allowed') {
          alert(language === 'traditional' ? '請允許語音權限' :
                language === 'simplified' ? '请允许语音权限' : 'Please allow speech permission');
        }
      }
    );
  };

  // Enhanced recording functions with better audio quality and format
  const startRecording = async () => {
    try {
      // Request high-quality audio
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        } 
      });
      
      // Use better audio format for compatibility
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';
      
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setRecordingUrl(url);
        setIsRecording(false);
        
        // Clean up the stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      recorder.onerror = (event) => {
        console.error('Recording error:', event);
        setIsRecording(false);
        alert(language === 'traditional' ? '錄音失敗' :
              language === 'simplified' ? '录音失败' : 'Recording failed');
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        alert(language === 'traditional' ? '請允許麥克風權限' :
              language === 'simplified' ? '请允许麦克风权限' : 'Please allow microphone permission');
      } else if (error.name === 'NotFoundError') {
        alert(language === 'traditional' ? '找不到麥克風設備' :
              language === 'simplified' ? '找不到麦克风设备' : 'No microphone found');
      } else {
        alert(language === 'traditional' ? '錄音啟動失敗' :
              language === 'simplified' ? '录音启动失败' : 'Failed to start recording');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      try {
        mediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping recording:', error);
        setIsRecording(false);
      }
    }
  };

  const playRecording = () => {
    if (recordingUrl) {
      try {
        const audio = new Audio(recordingUrl);
        
        // Add event listeners for better playback control
        audio.onloadstart = () => {
          console.log('Audio loading started');
        };
        
        audio.oncanplay = () => {
          console.log('Audio can play');
        };
        
        audio.onplay = () => {
          console.log('Audio playback started');
        };
        
        audio.onended = () => {
          console.log('Audio playback ended');
        };
        
        audio.onerror = (event) => {
          console.error('Audio playback error:', event);
          alert(language === 'traditional' ? '音頻播放失敗' :
                language === 'simplified' ? '音频播放失败' : 'Audio playback failed');
        };
        
        // Set audio properties for better playback
        audio.volume = 1.0;
        audio.playbackRate = 1.0;
        
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error('Audio play failed:', error);
            alert(language === 'traditional' ? '音頻播放失敗' :
                  language === 'simplified' ? '音频播放失败' : 'Audio playback failed');
          });
        }
      } catch (error) {
        console.error('Error playing recording:', error);
        alert(language === 'traditional' ? '音頻播放失敗' :
              language === 'simplified' ? '音频播放失败' : 'Audio playback failed');
      }
    }
  };

  const clearRecording = () => {
    if (recordingUrl) {
      URL.revokeObjectURL(recordingUrl);
      setRecordingUrl(null);
    }
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      try {
        mediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping media recorder:', error);
      }
    }
    setIsRecording(false);
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-4">
      {word.image && (
        <div className="w-full h-32 mb-4 rounded-lg overflow-hidden">
          <img 
            src={word.image} 
            alt={word.english}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      <div className="text-center mb-4">
        <h3 className="text-2xl font-bold text-white mb-2">{displayText.primary}</h3>
        <p className="text-lg text-gray-300 mb-2">{displayText.secondary}</p>
        <p className="text-sm text-gray-400">{displayText.pronunciation}</p>
      </div>

      {mode === 'read' && (
        <div className="mb-4">
          <p className="text-gray-300 text-sm leading-relaxed">{word.definition}</p>
          <div className="mt-3 p-3 bg-black/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-200 text-sm italic">"{word.example}"</p>
              <button
                onClick={() => speakText(word.example)}
                className={`p-2 rounded-lg transition-all ${
                  isPlaying && playingText === word.example
                    ? 'bg-orange-600 text-white' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                title={language === 'traditional' ? '發音' : 
                       language === 'simplified' ? '发音' : 'Pronounce'}
              >
                <span className="text-lg">{isPlaying && playingText === word.example ? '⏸️' : '🔊'}</span>
              </button>
            </div>
            
            {/* Recording Controls for Example Sentence */}
            <div className="flex items-center space-x-2 mt-2">
              {!isRecording && !recordingUrl && (
                <button
                  onClick={startRecording}
                  className="p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all"
                  title={language === 'traditional' ? '錄音' : 
                         language === 'simplified' ? '录音' : 'Record'}
                >
                  <span className="text-lg">🎤</span>
                </button>
              )}
              
              {isRecording && (
                <button
                  onClick={stopRecording}
                  className="p-2 rounded-lg bg-red-700 text-white transition-all animate-pulse"
                  title={language === 'traditional' ? '停止' : 
                         language === 'simplified' ? '停止' : 'Stop'}
                >
                  <span className="text-lg">⏹️</span>
                </button>
              )}
              
              {recordingUrl && (
                <>
                  <button
                    onClick={playRecording}
                    className="p-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-all"
                    title={language === 'traditional' ? '播放' : 
                           language === 'simplified' ? '播放' : 'Play'}
                  >
                    <span className="text-lg">▶️</span>
                  </button>
                  
                  <button
                    onClick={clearRecording}
                    className="p-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white transition-all"
                    title={language === 'traditional' ? '清除' : 
                           language === 'simplified' ? '清除' : 'Clear'}
                  >
                    <span className="text-lg">🗑️</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex space-x-3">
        <button 
          onClick={() => speakText(word.english)}
          className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-lg transition-all ${
            isPlaying && playingText === word.english
              ? 'bg-orange-600 text-white' 
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          <Volume2 className="w-5 h-5" />
          <span>{language === 'traditional' ? '聽發音' : 
                 language === 'simplified' ? '听发音' : 'Listen'}</span>
        </button>
        
        {mode === 'read' && (
          <>
            <button 
              className="flex-1 py-3 bg-green-500 rounded-lg"
              onClick={onStudyClick}
            >
              學習
            </button>
            <button 
              className="flex-1 py-3 bg-purple-500 rounded-lg"
              onClick={onTestClick}
            >
              測試
            </button>
          </>
        )}
        
        {mode === 'study' && (
          <button className="flex-1 flex items-center justify-center space-x-2 py-3 bg-green-500 rounded-lg">
            <Eye className="w-5 h-5" />
            <span>查看</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default VocabularyCard;
